import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { Typography } from '@scale/scaleui';
import { AuditorEvaluationData } from '@scale/shared/taskerScreening/types';
import API from '@scale/frontend-lib/dashboard-api';
import { CircularProgress } from 'react-scale/components/design-system';
import Table from 'corp/components/UI/Table';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';

interface TaskerScreeningAuditorEvaluationProps {
  user: IUserJson;
}

const tableHeaders: Record<string, string> = {
  auditorId: 'Auditor ID',
  auditorEmail: 'Auditor Email',
  numAudits: 'Num Audits',
  numBenchmarkAudits: 'Num Benchmarks Attempted',
  averageScore: 'Average All Scores Given',
  averageBenchmarkScore: 'Average Benchmark Given Score',
  averageBenchmarkExpectedScore: 'Average Benchmark Expected Score',
  averageAccuracy: 'Average Accuracy',
};

const shownTableHeaders = [
  'auditorId',
  'numAudits',
  'numBenchmarkAudits',
  'averageScore',
  'averageBenchmarkScore',
  'averageBenchmarkExpectedScore',
  'averageAccuracy',
];

const filterableTableHeaders = [
  'auditorId',
  'numAudits',
  'numBenchmarkAudits',
  'averageScore',
  'averageBenchmarkScore',
  'averageBenchmarkExpectedScore',
  'averageAccuracy',
];

const TaskerScreeningAuditorEvaluation: React.FC<TaskerScreeningAuditorEvaluationProps> = () => {
  const [auditorData, setAuditorData] = useState<AuditorEvaluationData[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);

  const handleFetchAuditorEvaluationData = useCallback(async () => {
    setLoadingTable(true);
    const { auditorData } = await API.genai.fetchAuditorEvaluationData({});
    setAuditorData(auditorData);
    setLoadingTable(false);
  }, []);

  useEffect(() => {
    handleFetchAuditorEvaluationData();
  }, [handleFetchAuditorEvaluationData]);

  const tableData = useMemo(() => {
    const data = auditorData.map(auditor => ({
      auditorId: <SmartLink type={INamedObjectTypes.User} id={auditor.auditorId} />,
      auditorEmail: auditor.auditorEmail,
      numAudits: auditor.numAudits,
      numBenchmarkAudits: auditor.numBenchmarkAudits,
      averageScore: auditor.averageScore,
      averageBenchmarkScore: auditor.averageBenchmarkScore,
      averageBenchmarkExpectedScore: auditor.averageBenchmarkExpectedScore,
      averageAccuracy: auditor.averageAccuracy,
    }));
    return data;
  }, [auditorData]);

  const columns = useMemo(() => {
    return Object.keys(tableHeaders).map(label => ({
      Header: tableHeaders[label],
      accessor: label,
      show: shownTableHeaders.includes(label),
      filterable: filterableTableHeaders.includes(label),
    }));
  }, []);

  return (
    <div className="scaleui scale-theme scale-tailwind p-4">
      <div className="mb-16">
        <Typography.H4>Evaluate Auditors</Typography.H4>
        <p>
          Use this table to review auditors. You can view their auditing stats, benchmark
          accuracies, and (soon) time spent per audits.
        </p>
        <div className="mt-8">
          {loadingTable ? (
            <CircularProgress />
          ) : (
            <Table
              filterable
              data={tableData}
              columns={columns}
              defaultFilterMethod={(filter, row) => {
                const rowValue =
                  filter.id === 'auditorId'
                    ? `${row[filter.id].props.id}${row.createdByEmail}`
                    : filter.id === 'qualificationName'
                    ? `${row[filter.id]}${row.qualification}`
                    : row[filter.id];
                return (rowValue ?? '').toLowerCase().includes(filter.value.toLowerCase());
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskerScreeningAuditorEvaluation;
